import { Head, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import { ArrowLeftIcon, PaperClipIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';

export default function Edit({ visitor, exhibitions, flash }) {
    const fileInputRef = useRef(null);
    const [newFileNames, setNewFileNames] = useState([]);

    const { data, setData, post, processing, errors, progress } = useForm({
        _method: 'PUT',
        personal_contact_name: visitor.personal_contact_name || '',
        email: visitor.email || '',
        company_name: visitor.company_name || '',
        type: visitor.type || '',
        exhibition_id: visitor.exhibition_id || '',
        telephone_country_code: visitor.telephone_country_code || '',
        telephone: visitor.telephone || '',
        fax: visitor.fax || '',
        website_url: visitor.website_url || '',
        country: visitor.country || '',
        mobile_country_code: visitor.mobile_country_code || '',
        mobile_number: visitor.mobile_number || '',
        whatsapp_country_code: visitor.whatsapp_country_code || '',
        whatsapp_number: visitor.whatsapp_number || '',
        attachments: [],
    });

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setData('attachments', files);
        setNewFileNames(files.map(f => f.name));
    };

    const removeNewFiles = () => {
        setData('attachments', []);
        setNewFileNames([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const submit = (e) => {
        e.preventDefault();
        post(`/visitors/${visitor.id}`, {
            forceFormData: true,
        });
    };

    return (
        <AppLayout>
            <Head title="Edit Visitor" />
            <PageHeader
                title="Edit Visitor"
                subtitle={`Editing ${visitor.personal_contact_name || visitor.company_name || 'Visitor'}`}
                actions={
                    <Button href="/visitors" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <form onSubmit={submit} className="space-y-6">
                {/* Personal Information */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Personal Information</h3>
                    </div>
                    <CardBody>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput
                                label="Contact Name"
                                value={data.personal_contact_name}
                                onChange={e => setData('personal_contact_name', e.target.value)}
                                error={errors.personal_contact_name}
                                placeholder="Full name"
                            />
                            <FormInput
                                label="Email"
                                type="email"
                                value={data.email}
                                onChange={e => setData('email', e.target.value)}
                                error={errors.email}
                                placeholder="email@example.com"
                            />
                            <FormSelect
                                label="Type"
                                value={data.type}
                                onChange={e => setData('type', e.target.value)}
                                error={errors.type}
                            >
                                <option value="">Select type...</option>
                                <option value="Customer">Customer</option>
                                <option value="Supplier">Supplier</option>
                            </FormSelect>
                            <FormSelect
                                label="Exhibition"
                                value={data.exhibition_id}
                                onChange={e => setData('exhibition_id', e.target.value)}
                                error={errors.exhibition_id}
                            >
                                <option value="">Select exhibition...</option>
                                {exhibitions?.map(ex => (
                                    <option key={ex.id} value={ex.id}>
                                        {ex.name} {ex.is_default ? '(Default)' : ''}
                                    </option>
                                ))}
                            </FormSelect>
                        </div>
                    </CardBody>
                </Card>

                {/* Company Information */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Company Information</h3>
                    </div>
                    <CardBody>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput
                                label="Company Name"
                                value={data.company_name}
                                onChange={e => setData('company_name', e.target.value)}
                                error={errors.company_name}
                                placeholder="Company name"
                            />
                            <FormInput
                                label="Website"
                                type="url"
                                value={data.website_url}
                                onChange={e => setData('website_url', e.target.value)}
                                error={errors.website_url}
                                placeholder="https://example.com"
                            />
                            <FormInput
                                label="Country"
                                value={data.country}
                                onChange={e => setData('country', e.target.value)}
                                error={errors.country}
                                placeholder="Country"
                            />
                            <FormInput
                                label="Fax"
                                value={data.fax}
                                onChange={e => setData('fax', e.target.value)}
                                error={errors.fax}
                                placeholder="Fax number"
                            />
                        </div>
                    </CardBody>
                </Card>

                {/* Phone Numbers */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Phone Numbers</h3>
                    </div>
                    <CardBody>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex gap-3">
                                <FormInput
                                    label="Telephone Code"
                                    value={data.telephone_country_code}
                                    onChange={e => setData('telephone_country_code', e.target.value)}
                                    error={errors.telephone_country_code}
                                    placeholder="+1"
                                    className="w-28"
                                />
                                <FormInput
                                    label="Telephone"
                                    value={data.telephone}
                                    onChange={e => setData('telephone', e.target.value)}
                                    error={errors.telephone}
                                    placeholder="Phone number"
                                    className="flex-1"
                                />
                            </div>
                            <div className="flex gap-3">
                                <FormInput
                                    label="Mobile Code"
                                    value={data.mobile_country_code}
                                    onChange={e => setData('mobile_country_code', e.target.value)}
                                    error={errors.mobile_country_code}
                                    placeholder="+1"
                                    className="w-28"
                                />
                                <FormInput
                                    label="Mobile Number"
                                    value={data.mobile_number}
                                    onChange={e => setData('mobile_number', e.target.value)}
                                    error={errors.mobile_number}
                                    placeholder="Mobile number"
                                    className="flex-1"
                                />
                            </div>
                            <div className="flex gap-3">
                                <FormInput
                                    label="WhatsApp Code"
                                    value={data.whatsapp_country_code}
                                    onChange={e => setData('whatsapp_country_code', e.target.value)}
                                    error={errors.whatsapp_country_code}
                                    placeholder="+1"
                                    className="w-28"
                                />
                                <FormInput
                                    label="WhatsApp Number"
                                    value={data.whatsapp_number}
                                    onChange={e => setData('whatsapp_number', e.target.value)}
                                    error={errors.whatsapp_number}
                                    placeholder="WhatsApp number"
                                    className="flex-1"
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Existing Attachments */}
                {visitor.attachments && visitor.attachments.length > 0 && (
                    <Card>
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-800">Existing Attachments</h3>
                        </div>
                        <CardBody>
                            <div className="space-y-2">
                                {visitor.attachments.map((att, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
                                        <PaperClipIcon className="w-4 h-4 text-slate-400" />
                                        <span className="flex-1 truncate">{att.name}</span>
                                        <span className="text-xs text-slate-400">
                                            {att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* New Attachments */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Add New Attachments</h3>
                    </div>
                    <CardBody>
                        <div className="space-y-3">
                            <div
                                className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-600 transition-colors"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <PaperClipIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Click to upload additional files</p>
                                <p className="text-xs text-slate-400 mt-1">Max 1GB per file</p>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            {newFileNames.length > 0 && (
                                <div className="space-y-2">
                                    {newFileNames.map((name, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-emerald-50 px-3 py-2 rounded-lg">
                                            <PaperClipIcon className="w-4 h-4 text-emerald-700" />
                                            <span className="flex-1 truncate">{name}</span>
                                            <span className="text-xs text-emerald-800">New</span>
                                        </div>
                                    ))}
                                    <button type="button" onClick={removeNewFiles} className="text-xs text-red-500 hover:text-red-700">
                                        <XMarkIcon className="w-3 h-3 inline" /> Remove new files
                                    </button>
                                </div>
                            )}
                            {progress && (
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div className="bg-emerald-700 h-2 rounded-full transition-all" style={{ width: `${progress.percentage}%` }} />
                                </div>
                            )}
                        </div>
                    </CardBody>
                </Card>

                {/* Submit */}
                <div className="flex gap-3">
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button href="/visitors" variant="outline">Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
